import type { Key, ReactElement } from 'react';
import {
  Button,
  FieldError,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select as AriaSelect,
  SelectValue,
  Text,
} from 'react-aria-components';
import { Icon } from './Icon';
import s from './fields.module.css';

export interface SelectOption {
  id: string;
  label: string;
  /** Listed but not choosable (say why in the label). */
  isDisabled?: boolean;
}

export interface SelectProps {
  label: string;
  options: readonly SelectOption[];
  /** Selected option id (controlled). */
  value?: string | null;
  defaultValue?: string;
  onChange?: (id: string | null) => void;
  placeholder?: string;
  description?: string;
  errorMessage?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  /** The caller decides when `errorMessage` shows (a choice outside the form it confirms). */
  isInvalid?: boolean;
  name?: string;
}

/** A labelled single-choice picker (React Aria Select: button + listbox popover). */
export function Select({
  label,
  options,
  value,
  defaultValue,
  onChange,
  placeholder = 'Choose…',
  description,
  errorMessage,
  isRequired = false,
  isDisabled = false,
  isInvalid,
  name,
}: SelectProps): ReactElement {
  return (
    <AriaSelect
      className={s.field}
      placeholder={placeholder}
      isRequired={isRequired}
      isDisabled={isDisabled}
      {...(isInvalid !== undefined && { isInvalid })}
      {...(name !== undefined && { name })}
      {...(value !== undefined && { value })}
      {...(defaultValue !== undefined && { defaultValue })}
      onChange={(key: Key | null) => {
        onChange?.(key === null ? null : String(key));
      }}
    >
      <Label className={s.label}>{label}</Label>
      <div className={s.body}>
        <Button className={s.selectButton}>
          <SelectValue className={s.selectValue} />
          <Icon name="chevronDown" size={16} />
        </Button>
        {description !== undefined && (
          <Text slot="description" className={s.description}>
            {description}
          </Text>
        )}
        <FieldError className={s.error}>{errorMessage}</FieldError>
      </div>
      <Popover className={s.popover}>
        <ListBox className={s.listbox} items={options}>
          {(option) => (
            <ListBoxItem
              id={option.id}
              textValue={option.label}
              className={s.option}
              isDisabled={option.isDisabled === true}
            >
              {option.label}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
}
